/**
 * auto-captions-canvas-captions.js — рисовальщик надписей для АППАРАТНОГО
 * прожига (WebCodecs): та же модель строк и слов, что у превью в плеере и у
 * ASS-сборщика, но кадр рисуется на 2D-канве поверх декодированного кадра и
 * уходит в VideoEncoder (H.264 на GPU), а не в libx264 внутри WASM.
 *
 * ЗАЧЕМ. Замер 09.09.2026 на 106-секундном 1080×1920: прожиг через FFmpeg-WASM
 * (libass + libx264, один поток) — 991 с, т. е. 9.4× длины ролика; тот же ролик
 * через WebCodecsPipeline с 2D-наложением — 47.7 с (0.45×), в 20.8 раза быстрее.
 * Прод-телеметрия (exec_ms, с 08.09): медиана 9.1×, на телефоне 18.2×.
 *
 * ПАРИТЕТ С ПРЕВЬЮ. Геометрия — та же, что у ASS-сборщика: PlayRes 1080 по
 * высоте (кегль SIZE_MAP × h/1080), отступ снизу 10 %, сверху 8 %, обводка 4 ед.
 * Анимации активного слова — по таймингам CSS превью (.35 с Hormozi, .55 с
 * Bounce и т. д.), а не по ASS-версиям: превью — это то, что человеку обещали.
 * Канва, в отличие от ASS, масштабирует слово БЕЗ перекладки строки — как и
 * CSS transform в превью.
 *
 * Контракт:
 *   window.__acCanvasCaptions.create(lines, opts) → Promise<{ draw(ctx,w,h,t), fontFamily, substituted }>
 *     lines — state.lines ([{start,end,words:[{text,start,end,emphasis,isEmoji,_speakerColor}]}])
 *     opts  — { preset, color, font, size, position, stroke, shadow, speakers }
 *   window.__acCanvasCaptions.eligible(state, flags) → true, если этот рисовальщик
 *     умеет ВСЁ, что просят у прожига (иначе — FFmpeg-лейн, как раньше).
 */
(function () {
    'use strict';

    var SIZE_MAP = { sm: 52, md: 72, lg: 92, xl: 112 };     // = ass-builder.js, PlayResY 1080
    var PLAY_H = 1080;

    var FONT_FILES = {
        'Inter':            '/vendor/fonts/Inter-Bold.ttf',
        'Oswald':           '/vendor/fonts/Oswald-Bold.ttf',
        'Bebas Neue':       '/vendor/fonts/BebasNeue-Regular.ttf',
        'Anton':            '/vendor/fonts/Anton-Regular.ttf',
        'Permanent Marker': '/vendor/fonts/PermanentMarker-Regular.ttf',
        'Noto Sans':        '/vendor/fonts/NotoSans-Bold.ttf',
        'Russo One':        '/vendor/fonts/RussoOne-Regular.ttf',
        'Rubik':            '/vendor/fonts/Rubik-Black.ttf',
        'Caveat':           '/vendor/fonts/Caveat-Bold.ttf',
        'Noto Sans JP':     '/vendor/fonts/NotoSansJP-Bold.ttf',
        'Noto Sans KR':     '/vendor/fonts/NotoSansKR-Bold.ttf',
        'Noto Sans SC':     '/vendor/fonts/NotoSansSC-Bold.ttf',
        'Noto Sans Arabic': '/vendor/fonts/NotoSansArabic-Bold.ttf',
    };
    var FONT_MAP = {
        inter: 'Inter', oswald: 'Oswald', bebas: 'Bebas Neue', impact: 'Anton', noto: 'Noto Sans',
        marker: 'Permanent Marker', russo: 'Russo One', rubik: 'Rubik', caveat: 'Caveat', system: 'Arial',
        roboto: 'Inter', montserrat: 'Inter', poppins: 'Inter',
    };
    var loadedFonts = {};

    function loadFont(family) {
        var url = FONT_FILES[family];
        if (!url || typeof FontFace === 'undefined' || !document.fonts) return Promise.resolve(false);
        if (loadedFonts[family]) return loadedFonts[family];
        var ver = (window.__vendorFontsVersion ? ('?v=' + window.__vendorFontsVersion) : '');
        var face = new FontFace('AC ' + family, 'url(' + url + ver + ')', { weight: '400', style: 'normal' });
        loadedFonts[family] = face.load().then(function (f) { document.fonts.add(f); return true; })
            .catch(function (e) { console.warn('[ac-canvas] font load failed', family, e && e.message); return false; });
        return loadedFonts[family];
    }

    function hexToRgb(hex) {
        var h = String(hex || '#FFFFFF').replace('#', '');
        if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
        var n = parseInt(h, 16);
        if (!isFinite(n)) n = 0xFFFFFF;
        return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
    }
    function rgba(hex, a) { var c = hexToRgb(hex); return 'rgba(' + c[0] + ',' + c[1] + ',' + c[2] + ',' + (a == null ? 1 : a) + ')'; }
    function mix(hexA, hexB, t) {
        var a = hexToRgb(hexA), b = hexToRgb(hexB);
        return 'rgb(' + Math.round(a[0] + (b[0] - a[0]) * t) + ',' + Math.round(a[1] + (b[1] - a[1]) * t) + ',' + Math.round(a[2] + (b[2] - a[2]) * t) + ')';
    }
    function easeOut(x) { x = Math.max(0, Math.min(1, x)); return 1 - (1 - x) * (1 - x); }
    function overshoot(x) { x = Math.max(0, Math.min(1, x)); var c = 1.56; return 1 + (c + 1) * Math.pow(x - 1, 3) + c * Math.pow(x - 1, 2); } // cubic-bezier(.34,1.56,.64,1)-ish

    /* Пресеты: базовый цвет, верхний регистр, притушение прошлых слов и
       анимация активного слова → {scale, sy, dy(em), rot(deg), color, alpha}. */
    var PRESETS = {
        hormozi:  { upper: true,  past: 0.78, active: function (r, c) { return { sy: 1 + 0.2 * (1 - easeOut(r / 0.35)), color: c.active }; } },
        bounce:   { upper: false, past: 0.78, active: function (r, c) { var p = r / 0.55; var dy = p < 0.5 ? -0.28 * Math.sin(p / 0.5 * Math.PI) : -0.08 * Math.sin((p - 0.5) / 0.5 * Math.PI); return { dy: p < 1 ? dy : 0, color: c.active }; } },
        shake:    { upper: false, past: 0.78, active: function (r, c, w) { var p = r / 0.45; var seed = (Math.floor(w.start * 1000) % 6) - 3 || 2; var rot = p < 1 ? seed * Math.sin(p * Math.PI * 3) * (1 - p) : 0; return { rot: rot, color: c.active }; } },
        tiktok:   { upper: false, past: 1,    box: true, base: '#FFFFFF', active: function (r, c) { return { scale: 1 + 0.12 * (1 - easeOut(r / 0.42)), color: '#FFFFFF' }; } },
        classic:  { upper: false, past: 1,    base: '#FFFFFF', outline: 3, active: function (r, c) { return { color: c.active }; } },
        pop:      { upper: true,  past: 0.78, active: function (r, c) { var p = r / 0.4; var s = p < 1 ? 1.6 - 0.6 * overshoot(p) : 1; return { scale: s, color: c.active }; } },
        fade:     { upper: false, past: 1,    base: '#FFFFFF', lineFade: 0.16, active: function (r, c) { return { color: c.active }; } },
        mrbeast:  { upper: true,  past: 0.875, base: '#FFEB3B', outline: 10, activeColor: '#FFFFFF', active: function (r, c) { return { sy: 1 + 0.3 * (1 - easeOut(r / 0.25)), color: '#FFFFFF' }; } },
        slideup:  { upper: false, past: 1,    base: '#FFFFFF', lineFade: 0.15, slide: 0.2, active: function (r, c) { return { color: c.active }; } },
        gradient: { upper: false, past: 0.78, active: function (r, c, w) { var d = Math.max(0.15, w.end - w.start); var p = Math.max(0, Math.min(1, r / d)); var col = p < 0.5 ? mix('#00E5FF', '#FFEB3B', p * 2) : mix('#FFEB3B', '#FF4081', (p - 0.5) * 2); return { color: col }; } },
        karaoke:  { upper: false, past: 1,    karaoke: true, base: '#FFFFFF' },
    };

    function sampleTextOf(lines) {
        var s = '';
        for (var i = 0; i < lines.length && s.length < 400; i++) {
            var ws = lines[i].words || [];
            for (var j = 0; j < ws.length && s.length < 400; j++) if (!ws[j].isEmoji) s += ws[j].text + ' ';
        }
        return s;
    }

    function pickFamily(lines, opts) {
        var requested = FONT_MAP[opts.font] || ((opts.preset === 'hormozi' || opts.preset === 'pop') ? 'Bebas Neue' : 'Inter');
        var cover = null;
        try {
            if (window.ASSBuilder && typeof window.ASSBuilder.coveringFontFor === 'function') {
                cover = window.ASSBuilder.coveringFontFor(sampleTextOf(lines), requested);
            }
        } catch (_) { cover = null; }
        return { family: cover || requested, substituted: cover ? { from: requested, to: cover } : null };
    }

    function create(lines, opts) {
        opts = opts || {};
        var preset = PRESETS[opts.preset] ? opts.preset : 'hormozi';
        var P = PRESETS[preset];
        var pf = pickFamily(lines, opts);
        var family = pf.family;
        var activeColor = P.activeColor || opts.color || '#FFEB3B';
        var baseColor = P.base || '#FFFFFF';
        var stroke = opts.stroke !== false;
        var shadow = !!opts.shadow;
        var position = opts.position || 'bottom';
        var sizePt = SIZE_MAP[opts.size] || SIZE_MAP.md;
        var speakersOn = !!opts.speakers;

        // Плоский список «показов»: для каждой строки — окно и слова без эмодзи.
        var shows = [];
        for (var i = 0; i < lines.length; i++) {
            var ln = lines[i];
            var words = (ln.words || []).filter(function (w) { return !w.isEmoji && String(w.text || '').trim() !== ''; });
            if (!words.length) continue;
            shows.push({ start: ln.start, end: ln.end, words: words });
        }
        shows.sort(function (a, b) { return a.start - b.start; });

        function findShow(t) {
            var lo = 0, hi = shows.length - 1;
            while (lo <= hi) {
                var mid = (lo + hi) >> 1, s = shows[mid];
                if (t < s.start) hi = mid - 1;
                else if (t > s.end + 0.05) lo = mid + 1;
                else return s;
            }
            return null;
        }

        var measureCache = {};
        function layout(ctx, show, fontPx, maxW) {
            // Разбивка на ряды — жадная по ширине (WrapStyle 0 в ASS тоже
            // переносит по ширине); один ряд, если помещается.
            var key = fontPx + '|' + maxW;
            if (show._layoutKey === key) return show._layout;
            var space = ctx.measureText(' ').width;
            var rows = [], row = [], rowW = 0;
            for (var i = 0; i < show.words.length; i++) {
                var w = show.words[i];
                var text = P.upper ? String(w.text).toUpperCase() : String(w.text);
                var mw = ctx.measureText(text).width;
                var emph = (w.emphasis === 'high') ? 1.15 : (w.emphasis === 'med') ? 1.07 : 1;
                var item = { w: w, text: text, width: mw, emph: emph };
                if (row.length && rowW + space + mw > maxW) { rows.push({ items: row, width: rowW }); row = []; rowW = 0; }
                if (row.length) rowW += space;
                row.push(item); rowW += mw;
            }
            if (row.length) rows.push({ items: row, width: rowW });
            show._layoutKey = key; show._layout = { rows: rows, space: space };
            return show._layout;
        }

        function activeIndex(show, t) {
            for (var i = 0; i < show.words.length; i++) {
                var w = show.words[i];
                if (t >= w.start - 0.02 && t <= w.end + 0.05) return i;
                if (t < w.start) return -1;
            }
            return -1;
        }

        function draw(ctx, w, h, t) {
            var show = findShow(t);
            if (!show) return;
            var scale = h / PLAY_H;
            var fontPx = Math.round(sizePt * scale);
            var lineH = fontPx * 1.2;
            var outlineW = (P.outline != null ? P.outline : (stroke ? 4 : 0)) * scale * 2;   // ASS border = per-side
            var shadowOff = shadow ? 3 * scale : 0;
            var font = 'bold ' + fontPx + 'px "AC ' + family + '", "' + family + '", Arial, sans-serif';
            ctx.save();
            ctx.font = font;
            ctx.textBaseline = 'alphabetic';
            ctx.textAlign = 'left';
            ctx.lineJoin = 'round';
            ctx.miterLimit = 2;

            var L = layout(ctx, show, fontPx, w * 0.92);
            var rows = L.rows, space = L.space;
            var blockH = rows.length * lineH;
            var baseY; // базовая линия ПЕРВОГО ряда
            if (position === 'top') baseY = Math.round(h * 0.08) + fontPx * 0.9;
            else if (position === 'center') baseY = Math.round(h / 2 - blockH / 2) + fontPx * 0.9;
            else baseY = Math.round(h - h * 0.10) - blockH + fontPx * 0.9;

            // Анимации уровня строки: fade и slide-up.
            var lineAlpha = 1, lineDy = 0;
            var sinceStart = t - show.start, toEnd = show.end - t;
            if (P.lineFade) {
                lineAlpha = Math.min(1, Math.max(0, Math.min(sinceStart / P.lineFade, toEnd / P.lineFade + 1)));
            }
            if (P.slide) lineDy = fontPx * 0.6 * (1 - easeOut(sinceStart / P.slide));
            ctx.globalAlpha = lineAlpha;

            var ai = activeIndex(show, t);
            // Глобальный индекс активного слова в плоском списке
            var flatIdx = 0;
            for (var r = 0; r < rows.length; r++) {
                var row = rows[r];
                var x = Math.round((w - row.width) / 2);
                var y = baseY + r * lineH + lineDy;
                if (P.box) {
                    var padX = fontPx * 0.35, padY = fontPx * 0.25;
                    ctx.save();
                    ctx.fillStyle = 'rgba(0,0,0,0.6)';
                    roundRect(ctx, x - padX, y - fontPx * 0.9 - padY, row.width + padX * 2, fontPx * 1.15 + padY * 2, fontPx * 0.25);
                    ctx.fill();
                    ctx.restore();
                }
                for (var k = 0; k < row.items.length; k++) {
                    var it = row.items[k];
                    var isActive = flatIdx === ai, isPast = ai >= 0 && flatIdx < ai;
                    var color = speakersOn && it.w._speakerColor ? it.w._speakerColor : baseColor;
                    var alpha = isPast ? P.past : 1;
                    var sx = it.emph, sy = it.emph, dy = 0, rot = 0;
                    if (it.w.emphasis === 'high' && !isActive) color = '#FFEB3B';
                    var anim = null;
                    if (isActive && typeof P.active === 'function') {
                        anim = P.active(Math.max(0, t - it.w.start), { active: activeColor, base: baseColor }, it.w) || {};
                        if (anim.color) color = anim.color;
                        if (anim.scale) { sx *= anim.scale; sy *= anim.scale; }
                        if (anim.sy) sy *= anim.sy;
                        if (anim.dy) dy = anim.dy * fontPx;
                        if (anim.rot) rot = anim.rot;
                        alpha = 1;
                    }
                    var cx = x + it.width / 2, cy = y - fontPx * 0.35;
                    ctx.save();
                    ctx.globalAlpha = lineAlpha * alpha;
                    ctx.translate(cx, cy + dy);
                    if (rot) ctx.rotate(rot * Math.PI / 180);
                    ctx.scale(sx, sy);
                    ctx.translate(-cx, -cy);
                    if (P.karaoke) {
                        drawKaraokeWord(ctx, it, x, y, t, activeColor, baseColor, isPast, isActive, outlineW, shadowOff);
                    } else {
                        drawWord(ctx, it.text, x, y, color, outlineW, shadowOff);
                    }
                    ctx.restore();
                    x += it.width + space;
                    flatIdx++;
                }
            }
            ctx.restore();
        }

        function drawWord(ctx, text, x, y, color, outlineW, shadowOff) {
            if (shadowOff) {
                ctx.fillStyle = 'rgba(0,0,0,0.63)';
                ctx.fillText(text, x + shadowOff, y + shadowOff);
            }
            if (outlineW > 0) {
                ctx.lineWidth = outlineW;
                ctx.strokeStyle = '#000';
                ctx.strokeText(text, x, y);
            }
            ctx.fillStyle = color;
            ctx.fillText(text, x, y);
        }

        function drawKaraokeWord(ctx, it, x, y, t, active, base, isPast, isActive, outlineW, shadowOff) {
            // Прошлые — цветом подсветки, будущие — базовым, активное заливается слева направо.
            if (isPast) { drawWord(ctx, it.text, x, y, active, outlineW, shadowOff); return; }
            if (!isActive) { drawWord(ctx, it.text, x, y, base, outlineW, shadowOff); return; }
            var d = Math.max(0.15, it.w.end - it.w.start);
            var p = Math.max(0, Math.min(1, (t - it.w.start) / d));
            drawWord(ctx, it.text, x, y, base, outlineW, shadowOff);
            if (p > 0) {
                ctx.save();
                ctx.beginPath();
                ctx.rect(x - outlineW, y - it.width * 2, it.width * p + outlineW, it.width * 4);
                ctx.clip();
                ctx.fillStyle = active;
                ctx.fillText(it.text, x, y);
                ctx.restore();
            }
        }

        function roundRect(ctx, x, y, w, h, r) {
            ctx.beginPath();
            ctx.moveTo(x + r, y);
            ctx.arcTo(x + w, y, x + w, y + h, r);
            ctx.arcTo(x + w, y + h, x, y + h, r);
            ctx.arcTo(x, y + h, x, y, r);
            ctx.arcTo(x, y, x + w, y, r);
            ctx.closePath();
        }

        return loadFont(family).then(function () {
            return { draw: draw, fontFamily: family, substituted: pf.substituted, shows: shows.length };
        });
    }

    /* Умеет ли этот рисовальщик ВСЁ, что просят у прожига. Всё, чего он не
       умеет (эмодзи-картинки, вырезы по транскрипту, авто-кадрирование,
       авто-зум, бренд-кит, заглушение слов, перевод с оригиналом, учёт лица),
       остаётся у FFmpeg-лейна — он никуда не делся. */
    function eligible(state, flags) {
        flags = flags || {};
        if (!state || !state.lines || !state.lines.length) return false;
        if (!PRESETS[state.preset]) return false;
        if (flags.hasEmoji || flags.ebtCuts || flags.magicCut || flags.reframe || flags.autoZoom || flags.brandKit || flags.muteWord || flags.translateKeep || flags.faceAvoid) return false;
        return true;
    }

    window.__acCanvasCaptions = { create: create, eligible: eligible, PRESETS: Object.keys(PRESETS), FONT_MAP: FONT_MAP };
})();
